import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, useAdminAccess } from '../utilities/adminAccess';
import {
  clientFormProcessedBy,
  clientFormStatusLabelKey,
  clientFormTypeLabelKey,
  extractClientForm,
  formatClientFormDate,
} from '../utilities/clientFormsApi';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/clientForms.css';

const statusClass = (status) => {
  if (Number(status) === 1) return 'is-done';
  if (Number(status) === 2) return 'is-rejected';
  return 'is-new';
};

const Field = ({ label, children, wide }) => (
  <div className={`cf-detail-item${wide ? ' is-wide' : ''}`}>
    <label>{label}</label>
    {children}
  </div>
);

const textOrDash = (value, fallback) => {
  const text = String(value || '').trim();
  return text || fallback;
};

export default function ManageClientForm() {
  const { formId } = useParams();
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, loading: champsLoading, refreshPendingClientForms } = useChampionships();
  const { canReadChamp } = useAdminAccess('client_forms');

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    loadForm();
    return undefined;
  }, [formId, champId, champsLoading, canReadChamp]);

  const loadForm = async () => {
    setMessage({ text: '', type: '' });
    if (!canReadChamp) {
      setForm(null);
      setLoading(false);
      setMessage({ text: t('cfNoAccess'), type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminClientForm, {
        id: Number(formId),
      });
      const next = extractClientForm(response.data);
      if (!next) {
        setForm(null);
        setMessage({ text: t('cfNotFound'), type: 'error' });
      } else {
        setForm(next);
      }
    } catch (error) {
      setForm(null);
      setMessage({ text: getApiErrorMessage(error, t('cfLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (status) => {
    if (!form?.id || saving) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.setAdminClientFormStatus, {
        id: Number(form.id),
        status: Number(status),
      });
      const next = extractClientForm(response.data);
      if (next) setForm(next);
      setMessage({ text: t('cfStatusSaved'), type: 'success' });
      await refreshPendingClientForms();
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('cfStatusError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const crumbs = [
    { label: t('navClientForms'), to: '/client_forms' },
    { label: form?.name || t('cfDetailTitle') },
  ];

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('cfLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            {!form ? (
              <div className="st-empty">
                <h3>{t('cfNotFound')}</h3>
              </div>
            ) : (
              <section className="st-block">
                <div className="st-block-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0 }}>{t('cfDetailTitle')} #{form.id}</h2>
                  <span className={`cf-status ${statusClass(form.status)}`}>
                    {t(clientFormStatusLabelKey(form.status))}
                  </span>
                </div>

                <div className="cf-detail-grid">
                  <Field label={t('cfColType')}>
                    <span>{t(clientFormTypeLabelKey(form.form_type))}</span>
                  </Field>
                  <Field label={t('cfChampionship')}>
                    <span>{textOrDash(form.championship_name, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfColName')}>
                    <span>{textOrDash(form.name, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfPhone')}>
                    <span>{textOrDash(form.phone, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfEmail')}>
                    {form.email ? <a href={`mailto:${form.email}`}>{form.email}</a> : <span>{t('cfNoValue')}</span>}
                  </Field>
                  <Field label={t('cfCity')}>
                    <span>{textOrDash(form.city, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfContact')}>
                    <span>{textOrDash(form.contact, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfVk')}>
                    {form.vk_url ? (
                      <a href={form.vk_url} target="_blank" rel="noopener noreferrer">{form.vk_url}</a>
                    ) : (
                      <span>{t('cfNoValue')}</span>
                    )}
                  </Field>
                  <Field label={t('cfHowLearned')} wide>
                    <span>{textOrDash(form.how_learned, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfExperience')} wide>
                    <span>{textOrDash(form.experience, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfComment')} wide>
                    <span>{textOrDash(form.comment, t('cfNoValue'))}</span>
                  </Field>
                  <Field label={t('cfUserId')}>
                    <span>{form.user_id ? `#${form.user_id}` : t('cfNoValue')}</span>
                  </Field>
                  <Field label={t('cfCreatedAt')}>
                    <span>{formatClientFormDate(form.created_at, lang) || t('cfNoValue')}</span>
                  </Field>
                  <Field label={t('cfUpdatedAt')}>
                    <span>{formatClientFormDate(form.updated_at, lang) || t('cfNoValue')}</span>
                  </Field>
                  <Field label={t('cfProcessedBy')}>
                    <span>{clientFormProcessedBy(form) || t('cfNoValue')}</span>
                  </Field>
                  <Field label={t('cfProcessedAt')}>
                    <span>{formatClientFormDate(form.processed_at, lang) || t('cfNoValue')}</span>
                  </Field>
                </div>
              </section>
            )}
          </div>
        </div>

        {form ? (
          <div className="st-actions">
            <IfCanWrite entity="client_forms">
              {Number(form.status) !== 1 ? (
                <button
                  type="button"
                  className="st-btn st-btn-primary"
                  disabled={saving}
                  onClick={() => setStatus(1)}
                >
                  {saving ? t('cfLoading') : t('cfMarkDone')}
                </button>
              ) : null}
              {Number(form.status) !== 0 ? (
                <button
                  type="button"
                  className="st-btn st-btn-ghost"
                  disabled={saving}
                  onClick={() => setStatus(0)}
                >
                  {t('cfMarkNew')}
                </button>
              ) : null}
            </IfCanWrite>
          </div>
        ) : null}
      </div>
    </div>
  );
}
